import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Mail, 
  Users, 
  CreditCard, 
  TrendingUp, 
  Clock, 
  Plus, 
  Eye,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Crown
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const Subscriptions = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('newsletter');
  const isSuperAdmin = user?.role === 'admin';

  // Newsletter data
  const newsletterSubscribers = [
    { id: 1, email: 'sjohnson@dentalpractice.com', name: 'Dr. Sarah Johnson', status: 'active', joinedDate: '2025-01-15', openRate: '92%' },
    { id: 2, email: 'mchen@dentalcare.com', name: 'Dr. Michael Chen', status: 'active', joinedDate: '2025-02-20', openRate: '88%' },
    { id: 3, email: 'emma.w@email.com', name: 'Emma Williams', status: 'active', joinedDate: '2025-03-10', openRate: '95%' },
    { id: 4, email: 'john.davis@email.com', name: 'John Davis', status: 'unsubscribed', joinedDate: '2024-12-05', openRate: '45%' }
  ];

  // Membership plans
  const membershipPlans = [
    {
      id: 1,
      name: 'Basic Plan',
      price: '$49/month',
      features: ['5 Referrals/month', 'Basic Analytics', 'Email Support'],
      subscribers: 45,
      revenue: '$2,205',
      color: 'bg-blue-100 text-blue-800 border-blue-200'
    },
    {
      id: 2,
      name: 'Professional',
      price: '$99/month',
      features: ['20 Referrals/month', 'Advanced Analytics', 'Priority Support', 'Deal Rooms'],
      subscribers: 78,
      revenue: '$7,722',
      color: 'bg-purple-100 text-purple-800 border-purple-200'
    },
    {
      id: 3,
      name: 'Enterprise',
      price: '$249/month',
      features: ['Unlimited Referrals', 'Custom Analytics', '24/7 Support', 'White Label', 'API Access'],
      subscribers: 23,
      revenue: '$5,727',
      color: 'bg-gold-100 text-[#D4AF37] border-[#D4AF37]'
    }
  ];

  // Active memberships
  const activeMemberships = [
    {
      id: 1,
      user: 'Dr. Gerard Nikodem',
      email: 'gnikodem@practice.com',
      plan: 'Enterprise',
      status: 'active',
      startDate: '2024-06-01',
      nextBilling: '2025-12-01',
      amount: '$249',
      paymentMethod: 'Visa •••• 4242'
    },
    {
      id: 2,
      user: 'Dr. Sarah Johnson',
      email: 'sjohnson@dentalpractice.com',
      plan: 'Professional',
      status: 'active',
      startDate: '2025-01-15',
      nextBilling: '2025-12-15',
      amount: '$99',
      paymentMethod: 'Mastercard •••• 5555'
    },
    {
      id: 3,
      user: 'Dr. Michael Chen',
      email: 'mchen@dentalcare.com',
      plan: 'Professional',
      status: 'active',
      startDate: '2025-02-20',
      nextBilling: '2025-12-20',
      amount: '$99',
      paymentMethod: 'Amex •••• 1234'
    },
    {
      id: 4,
      user: 'Dr. Elizabeth Fassler',
      email: 'fasbeth@earthlink.net',
      plan: 'Basic Plan',
      status: 'trial',
      startDate: '2025-11-01',
      nextBilling: '2025-12-01',
      amount: '$49',
      paymentMethod: 'Not set'
    },
    {
      id: 5,
      user: 'Dr. David Schertzer',
      email: 'info@kennerlydental.com',
      plan: 'Professional',
      status: 'past_due',
      startDate: '2024-08-10',
      nextBilling: 'Overdue',
      amount: '$99',
      paymentMethod: 'Visa •••• 9876'
    }
  ];

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1 inline" />Active</Badge>;
      case 'trial':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200"><Clock className="h-3 w-3 mr-1 inline" />Trial</Badge>;
      case 'past_due':
        return <Badge className="bg-red-100 text-red-800 border-red-200"><AlertCircle className="h-3 w-3 mr-1 inline" />Past Due</Badge>;
      case 'unsubscribed':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200"><XCircle className="h-3 w-3 mr-1 inline" />Unsubscribed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const totalRevenue = membershipPlans.reduce((sum, plan) => {
    return sum + parseInt(plan.revenue.replace(/[$,]/g, ''));
  }, 0);

  const totalSubscribers = membershipPlans.reduce((sum, plan) => sum + plan.subscribers, 0);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
          <Mail className="h-8 w-8 mr-3 text-[#D4AF37]" />
          Subscriptions Management
        </h1>
        <p className="text-gray-600">
          {isSuperAdmin 
            ? 'Manage newsletter subscriptions and membership plans' 
            : 'Manage newsletter subscriptions'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6">
        <Button
          variant={activeTab === 'newsletter' ? 'default' : 'outline'}
          onClick={() => setActiveTab('newsletter')}
          className={activeTab === 'newsletter' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
        >
          <Mail className="h-4 w-4 mr-2" />
          Newsletter
        </Button>
        {isSuperAdmin && (
          <Button
            variant={activeTab === 'membership' ? 'default' : 'outline'}
            onClick={() => setActiveTab('membership')}
            className={activeTab === 'membership' ? 'bg-[#0A1628] text-[#D4AF37]' : ''}
          >
            <Crown className="h-4 w-4 mr-2" />
            Memberships
          </Button>
        )}
      </div>

      {/* Newsletter Tab */}
      {activeTab === 'newsletter' && (
        <>
          {/* Newsletter Stats */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Subscribers</p>
                    <p className="text-2xl font-bold text-[#0A1628]">{newsletterSubscribers.length}</p>
                  </div>
                  <Users className="h-8 w-8 text-[#D4AF37]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active</p>
                    <p className="text-2xl font-bold text-[#0A1628]">
                      {newsletterSubscribers.filter(s => s.status === 'active').length}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg Open Rate</p>
                    <p className="text-2xl font-bold text-[#0A1628]">87.4%</p>
                  </div>
                  <Eye className="h-8 w-8 text-[#D4AF37]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">This Month</p>
                    <p className="text-2xl font-bold text-[#0A1628]">+12</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Newsletter Subscribers List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Newsletter Subscribers</CardTitle>
                <Button size="sm" className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Subscriber
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subscriber</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Open Rate</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {newsletterSubscribers.map((subscriber) => (
                      <tr key={subscriber.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-[#0A1628]">{subscriber.name}</p>
                            <p className="text-sm text-gray-600">{subscriber.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(subscriber.status)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(subscriber.joinedDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0A1628]">
                          {subscriber.openRate}
                        </td>
                        <td className="px-6 py-4">
                          <Button variant="outline" size="sm">
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Membership Tab (SuperAdmin Only) */}
      {activeTab === 'membership' && isSuperAdmin && (
        <>
          {/* Membership Stats */}
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Members</p>
                    <p className="text-2xl font-bold text-[#0A1628]">{totalSubscribers}</p>
                  </div>
                  <Users className="h-8 w-8 text-[#D4AF37]" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Monthly Revenue</p>
                    <p className="text-2xl font-bold text-[#0A1628]">
                      ${(totalRevenue).toLocaleString()}
                    </p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active</p>
                    <p className="text-2xl font-bold text-[#0A1628]">
                      {activeMemberships.filter(m => m.status === 'active').length}
                    </p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Trials</p>
                    <p className="text-2xl font-bold text-[#0A1628]">
                      {activeMemberships.filter(m => m.status === 'trial').length}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Membership Plans */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {membershipPlans.map((plan) => (
              <Card key={plan.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{plan.name}</CardTitle>
                      <p className="text-3xl font-bold text-[#0A1628] mt-2">{plan.price}</p>
                    </div>
                    <Badge className={plan.color}>
                      {plan.subscribers} users
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-600">Monthly Revenue</p>
                    <p className="text-xl font-bold text-green-600">{plan.revenue}</p>
                  </div>
                  <Button className="w-full bg-[#0A1628] text-[#D4AF37] hover:bg-[#1a2332]">
                    Manage Plan
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Active Memberships List */}
          <Card>
            <CardHeader>
              <CardTitle>Active Memberships</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Billing</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {activeMemberships.map((membership) => (
                      <tr key={membership.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-[#0A1628] text-[#D4AF37]">
                                {membership.user.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm font-medium text-[#0A1628]">{membership.user}</p>
                              <p className="text-sm text-gray-600">{membership.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant="outline">{membership.plan}</Badge>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(membership.status)}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#0A1628]">
                          {membership.amount}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {membership.nextBilling}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {membership.paymentMethod}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm">
                              View
                            </Button>
                            <Button variant="outline" size="sm">
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Subscriptions;
